import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";

function numberOrNull(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatMoney(value, currency = "EUR") {
  if (value === null || value === undefined || value === "") return "";
  const symbol = currency === "USD" ? "$" : "€";
  return `${symbol}${Number(value || 0).toFixed(2)}`;
}

function recipeLabel(recipe, fallback = "") {
  return String(recipe?.name || recipe?.title || recipe?.recipe_name || fallback || "").trim();
}

function recipeId(recipe) {
  return recipe?.id ?? recipe?.recipe_id ?? null;
}

function normalizeRecipeResults(payload) {
  const body = payload?.data || payload || {};
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.recipes)) return body.recipes;
  if (Array.isArray(body.items)) return body.items;
  return [];
}

function itemName(item, fallback) {
  return item?.product_name || item?.name || item?.generic_product_name || fallback;
}

function warningText(warning, t) {
  if (!warning) return t("smart_kitchen.pantry_aware_preview.warning_fallback");
  return t(`smart_kitchen.pantry_aware_preview.warnings.${warning.key}`, {
    recipeId: warning?.params?.recipe_id || warning?.recipe_id || "",
    defaultValue: warning.message || warning.key || t("smart_kitchen.pantry_aware_preview.warning_fallback"),
  });
}

function recommendationText(recommendation, t) {
  if (!recommendation?.key) return "";
  return t(`smart_kitchen.pantry_aware_preview.recommendations.${recommendation.key}`, {
    defaultValue: recommendation.key,
  });
}

function explanationText(explanation, t) {
  if (!explanation?.key) return explanation?.message || "";
  return t(`smart_kitchen.pantry_aware_preview.explanations.${explanation.key}`, {
    count: explanation?.params?.count ?? 0,
    total: explanation?.params?.total ?? 0,
    defaultValue: explanation.message || explanation.key,
  });
}

function StatCard({ icon, title, children, testId }) {
  return (
    <Card sx={{ height: "100%", borderRadius: 1 }} data-testid={testId}>
      <CardContent>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              alignItems: "center",
              bgcolor: "action.hover",
              borderRadius: 1,
              display: "inline-flex",
              height: 38,
              justifyContent: "center",
              width: 38,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function SimpleList({ items = [], emptyText, renderItem, testId }) {
  return items.length ? (
    <List dense disablePadding data-testid={testId}>
      {items.map((item, index) => (
        <ListItem key={`${itemName(item, "item")}-${index}`} disableGutters>
          <ListItemText primary={renderItem ? renderItem(item, index) : itemName(item, emptyText)} />
        </ListItem>
      ))}
    </List>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {emptyText}
    </Typography>
  );
}

function ExplanationList({ title, items = [], emptyText, testId }) {
  const { t } = useTranslation();
  return (
    <Box data-testid={testId}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
        {title}
      </Typography>
      <SimpleList
        items={items}
        emptyText={emptyText}
        renderItem={(item) => explanationText(item, t)}
      />
    </Box>
  );
}

function RecipeSearchPicker({ selectedRecipe, pickerState, onInputChange, onSearch, onSelect, onClear }) {
  const { t } = useTranslation();
  const selectedName = recipeLabel(selectedRecipe);
  return (
    <Stack spacing={1.25}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "flex-start" }}>
        <Autocomplete
          value={selectedRecipe}
          inputValue={pickerState.query}
          options={pickerState.options || []}
          loading={pickerState.loading}
          sx={{ flex: 1 }}
          getOptionLabel={(option) => recipeLabel(option, t("smart_kitchen.pantry_aware_preview.unknown_recipe"))}
          isOptionEqualToValue={(option, value) => String(recipeId(option)) === String(recipeId(value))}
          onInputChange={(_event, value, reason) => {
            if (reason !== "reset") onInputChange(value);
          }}
          onChange={(_event, value) => onSelect(value)}
          noOptionsText={
            pickerState.error
              ? t("smart_kitchen.pantry_aware_preview.recipe_search_error")
              : pickerState.searched
                ? t("smart_kitchen.pantry_aware_preview.no_recipe_results")
                : t("smart_kitchen.pantry_aware_preview.recipe_search_empty")
          }
          loadingText={t("smart_kitchen.pantry_aware_preview.recipe_search_loading")}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label={t("smart_kitchen.pantry_aware_preview.recipe_search_label")}
              placeholder={t("smart_kitchen.pantry_aware_preview.recipe_search_placeholder")}
              inputProps={{
                ...params.inputProps,
                "data-testid": "pantry-aware-recipe-search",
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={`recipe-${recipeId(option) || recipeLabel(option)}`}>
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {recipeLabel(option, t("smart_kitchen.pantry_aware_preview.unknown_recipe"))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("smart_kitchen.pantry_aware_preview.recipe_result_meta", { id: recipeId(option) || t("smart_kitchen.empty_value") })}
                </Typography>
              </Stack>
            </li>
          )}
        />
        <Button
          variant="outlined"
          onClick={() => onSearch(pickerState.query)}
          disabled={pickerState.loading || pickerState.query.trim().length < 2}
          data-testid="pantry-aware-recipe-search-button"
          sx={{ minHeight: 56 }}
        >
          {pickerState.loading ? t("smart_kitchen.pantry_aware_preview.recipe_search_loading") : t("smart_kitchen.pantry_aware_preview.recipe_search_button")}
        </Button>
      </Stack>
      {selectedName ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
          <Chip
            color="primary"
            variant="outlined"
            label={t("smart_kitchen.pantry_aware_preview.selected_recipe", { name: selectedName })}
            data-testid="pantry-aware-selected-recipe"
          />
          <Button size="small" variant="text" onClick={onClear} data-testid="pantry-aware-clear-recipe">
            {t("smart_kitchen.pantry_aware_preview.clear_recipe")}
          </Button>
        </Stack>
      ) : null}
      {pickerState.error ? (
        <Alert severity="error" data-testid="pantry-aware-search-error">
          {pickerState.error}
        </Alert>
      ) : null}
    </Stack>
  );
}

export default function PantryAwarePlannerPreview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { country = "en" } = useParams();
  const [mode, setMode] = useState("candidate");
  const [mealPlanId, setMealPlanId] = useState("");
  const [form, setForm] = useState({
    recipe_id: "",
    recipe_name: "",
    servings: "4",
    date: new Date().toISOString().slice(0, 10),
    meal_type: "dinner",
  });
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [picker, setPicker] = useState({ query: "", options: [], loading: false, error: "", searched: false });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const previewData = preview?.data || preview || {};
  const pantry = previewData.pantry_utilization || {};
  const waste = previewData.waste_reduction || {};
  const shopping = previewData.shopping_impact || {};
  const splitMeals = previewData.split_meals || {};
  const warnings = previewData.warnings || [];
  const recommendations = previewData.recommendations || [];
  const currency = shopping.currency || "EUR";
  const pantryExplanations = pantry.explanations || [];
  const wasteExplanations = waste.explanations || [];
  const shoppingExplanations = shopping.explanations || [];

  const payload = useMemo(() => {
    if (mode === "meal_plan") {
      return { meal_plan_id: numberOrNull(mealPlanId) };
    }
    return {
      items: [
        {
          recipe_id: numberOrNull(form.recipe_id),
          servings: numberOrNull(form.servings) || 1,
          date: form.date,
          meal_type: form.meal_type,
        },
      ],
    };
  }, [form, mealPlanId, mode]);

  const searchRecipes = useCallback(async (rawQuery) => {
    const query = String(rawQuery || "").trim();
    if (query.length < 2) {
      setPicker((current) => ({ ...current, options: [], loading: false, error: "", searched: false }));
      return;
    }
    setPicker((current) => ({ ...current, loading: true, error: "", searched: true }));
    try {
      const response = await api.recipes.findByName(query);
      setPicker((current) => ({ ...current, options: normalizeRecipeResults(response), loading: false, error: "", searched: true }));
    } catch (err) {
      setPicker((current) => ({
        ...current,
        options: [],
        loading: false,
        error: err?.response?.data?.message || err?.message || t("smart_kitchen.pantry_aware_preview.recipe_search_error"),
        searched: true,
      }));
    }
  }, [t]);

  useEffect(() => {
    const query = picker.query.trim();
    if (query.length < 2) return undefined;
    const timer = setTimeout(() => searchRecipes(query), 300);
    return () => clearTimeout(timer);
  }, [picker.query, searchRecipes]);

  const selectRecipe = (recipe) => {
    const id = recipeId(recipe);
    const name = recipeLabel(recipe);
    setSelectedRecipe(recipe);
    setPicker((current) => ({ ...current, query: name, error: "" }));
    setForm((current) => ({ ...current, recipe_id: id ? String(id) : "", recipe_name: name }));
  };

  const clearRecipe = () => {
    setSelectedRecipe(null);
    setPicker({ query: "", options: [], loading: false, error: "", searched: false });
    setForm((current) => ({ ...current, recipe_id: "", recipe_name: "" }));
  };

  const runPreview = async (event) => {
    event.preventDefault();
    setError("");
    setPreview(null);
    const hasMealPlan = mode === "meal_plan" && numberOrNull(mealPlanId);
    const hasRecipe = mode === "candidate" && numberOrNull(form.recipe_id);
    if (!hasMealPlan && !hasRecipe) {
      setError(t("smart_kitchen.pantry_aware_preview.validation_required"));
      return;
    }
    setLoading(true);
    try {
      const response = await api.smartKitchen.getPantryAwarePreview(payload);
      setPreview(response?.data || {});
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || err?.isAuthFailure) {
        setError(t("smart_kitchen.pantry_aware_preview.login_required"));
      } else if (status === 404) {
        setError(t("smart_kitchen.pantry_aware_preview.not_found"));
      } else {
        setError(err?.response?.data?.message || t("smart_kitchen.pantry_aware_preview.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={3}>
        <Box>
          <Chip
            icon={<PreviewRoundedIcon />}
            label={t("smart_kitchen.pantry_aware_preview.experimental")}
            variant="outlined"
            sx={{ mb: 1.5 }}
          />
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {t("smart_kitchen.pantry_aware_preview.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
            {t("smart_kitchen.pantry_aware_preview.subtitle")}
          </Typography>
        </Box>

        <Alert severity="info">
          <Stack spacing={0.5}>
            <Typography variant="body2">{t("smart_kitchen.pantry_aware_preview.preview_only")}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>{t("smart_kitchen.pantry_aware_preview.no_change_copy")}</Typography>
            <Typography variant="body2">{t("smart_kitchen.pantry_aware_preview.improve_copy")}</Typography>
          </Stack>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 1 }}>
              <CardContent>
                <Stack component="form" spacing={2} onSubmit={runPreview}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      {t("smart_kitchen.pantry_aware_preview.form_title")}
                    </Typography>
                  </Stack>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    fullWidth
                    label={t("smart_kitchen.pantry_aware_preview.preview_mode")}
                    value={mode}
                    onChange={(event) => setMode(event.target.value)}
                    inputProps={{ "data-testid": "pantry-aware-preview-mode" }}
                  >
                    <option value="candidate">{t("smart_kitchen.pantry_aware_preview.mode_candidate")}</option>
                    <option value="meal_plan">{t("smart_kitchen.pantry_aware_preview.mode_meal_plan")}</option>
                  </TextField>

                  {mode === "meal_plan" ? (
                    <TextField
                      fullWidth
                      label={t("smart_kitchen.pantry_aware_preview.meal_plan_id")}
                      value={mealPlanId}
                      onChange={(event) => setMealPlanId(event.target.value)}
                      inputProps={{ inputMode: "numeric", "data-testid": "pantry-aware-meal-plan-id" }}
                    />
                  ) : (
                    <Stack spacing={2}>
                      <RecipeSearchPicker
                        selectedRecipe={selectedRecipe}
                        pickerState={picker}
                        onInputChange={(query) => setPicker((current) => ({
                          ...current,
                          query,
                          ...(query.trim().length < 2 ? { options: [], loading: false, error: "", searched: false } : {}),
                        }))}
                        onSearch={searchRecipes}
                        onSelect={selectRecipe}
                        onClear={clearRecipe}
                      />
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t("smart_kitchen.pantry_aware_preview.recipe_id")}
                            value={form.recipe_id}
                            onChange={(event) => setForm((current) => ({ ...current, recipe_id: event.target.value }))}
                            inputProps={{ inputMode: "numeric", "data-testid": "pantry-aware-recipe-id" }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t("smart_kitchen.pantry_aware_preview.servings")}
                            value={form.servings}
                            onChange={(event) => setForm((current) => ({ ...current, servings: event.target.value }))}
                            inputProps={{ inputMode: "numeric", "data-testid": "pantry-aware-servings" }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="date"
                            label={t("smart_kitchen.pantry_aware_preview.meal_date")}
                            value={form.date}
                            onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={t("smart_kitchen.pantry_aware_preview.meal_type")}
                            value={form.meal_type}
                            onChange={(event) => setForm((current) => ({ ...current, meal_type: event.target.value }))}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  )}

                  {error ? <Alert severity="error">{error}</Alert> : null}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button type="submit" variant="contained" disabled={loading} data-testid="pantry-aware-preview-button">
                      {loading ? t("smart_kitchen.pantry_aware_preview.previewing") : t("smart_kitchen.pantry_aware_preview.preview_button")}
                    </Button>
                    <Button variant="text" onClick={() => navigate(`/${country}/smart-kitchen`)}>
                      {t("smart_kitchen.pantry_aware_preview.back_to_smart_kitchen")}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            {previewData?.success ? (
              <Stack spacing={2} data-testid="pantry-aware-preview-result">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <StatCard icon={<Inventory2RoundedIcon color="primary" />} title={t("smart_kitchen.pantry_aware_preview.pantry_utilization")} testId="pantry-aware-utilization-card">
                      <Typography variant="h4" sx={{ fontWeight: 900 }}>
                        {t("smart_kitchen.pantry_aware_preview.score_value", { score: pantry.score || 0, max: pantry.max_score || 100 })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("smart_kitchen.pantry_aware_preview.used_count", {
                          used: pantry.used_inventory_items || 0,
                          total: pantry.total_inventory_items || 0,
                        })}
                      </Typography>
                      <Box sx={{ mt: 1.5 }}>
                        <ExplanationList
                          title={t("smart_kitchen.pantry_aware_preview.why_this_score")}
                          items={pantryExplanations}
                          emptyText={t("smart_kitchen.pantry_aware_preview.no_explanations")}
                          testId="pantry-aware-utilization-explanations"
                        />
                      </Box>
                      <Box sx={{ mt: 1.5 }}>
                        <SimpleList
                          items={pantry.used_items || []}
                          emptyText={t("smart_kitchen.pantry_aware_preview.no_used_items")}
                          renderItem={(item) => itemName(item, t("smart_kitchen.pantry_aware_preview.unknown_item"))}
                          testId="pantry-aware-used-items"
                        />
                      </Box>
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
                          {t("smart_kitchen.pantry_aware_preview.unused_items_title")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {t("smart_kitchen.pantry_aware_preview.unused_count", {
                            count: pantry.unused_inventory_items ?? (pantry.unused_items || []).length ?? 0,
                          })}
                        </Typography>
                        <SimpleList
                          items={pantry.unused_items || []}
                          emptyText={t("smart_kitchen.pantry_aware_preview.no_unused_items")}
                          renderItem={(item) => itemName(item, t("smart_kitchen.pantry_aware_preview.unknown_item"))}
                          testId="pantry-aware-unused-items"
                        />
                      </Box>
                    </StatCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StatCard icon={<EventBusyRoundedIcon color="primary" />} title={t("smart_kitchen.pantry_aware_preview.waste_reduction")} testId="pantry-aware-waste-card">
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Chip size="small" color={waste.level === "high" ? "warning" : waste.level === "medium" ? "info" : "success"} label={t(`smart_kitchen.pantry_aware_preview.risk_level.${waste.level || "low"}`)} />
                        <Typography variant="body2" color="text.secondary">
                          {t("smart_kitchen.pantry_aware_preview.risk_reduction", { value: waste.estimated_risk_reduction || 0 })}
                        </Typography>
                      </Stack>
                      <ExplanationList
                        title={t("smart_kitchen.pantry_aware_preview.risk_reduced_because")}
                        items={wasteExplanations}
                        emptyText={t("smart_kitchen.pantry_aware_preview.no_explanations")}
                        testId="pantry-aware-waste-explanations"
                      />
                      <Box sx={{ mt: 1.5 }}>
                      <SimpleList
                        items={previewData.expiring_products_used || waste.expiring_products_used || []}
                        emptyText={t("smart_kitchen.pantry_aware_preview.no_expiring_used")}
                        renderItem={(item) => t("smart_kitchen.pantry_aware_preview.expiring_item", {
                          item: itemName(item, t("smart_kitchen.pantry_aware_preview.unknown_item")),
                          days: item.days_until_expiry ?? t("smart_kitchen.empty_value"),
                        })}
                        testId="pantry-aware-expiring-items"
                      />
                      </Box>
                    </StatCard>
                  </Grid>
                  <Grid item xs={12}>
                    <StatCard icon={<ShoppingCartRoundedIcon color="primary" />} title={t("smart_kitchen.pantry_aware_preview.shopping_impact")} testId="pantry-aware-shopping-card">
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">{t("smart_kitchen.pantry_aware_preview.reduced_items")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{shopping.reduced_items || 0}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">{t("smart_kitchen.pantry_aware_preview.remaining_items")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{shopping.remaining_items_count || 0}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">{t("smart_kitchen.pantry_aware_preview.estimated_savings")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatMoney(shopping.estimated_savings, currency) || t("smart_kitchen.empty_value")}</Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 2 }}>
                        <ExplanationList
                          title={t("smart_kitchen.pantry_aware_preview.shopping_reduced_by")}
                          items={shoppingExplanations}
                          emptyText={t("smart_kitchen.pantry_aware_preview.no_explanations")}
                          testId="pantry-aware-shopping-explanations"
                        />
                      </Box>
                      <Box sx={{ mt: 1.5 }}>
                        <SimpleList
                          items={shopping.items_already_in_pantry || []}
                          emptyText={t("smart_kitchen.pantry_aware_preview.no_items_already_in_pantry")}
                          renderItem={(item) => itemName(item, t("smart_kitchen.pantry_aware_preview.unknown_item"))}
                          testId="pantry-aware-shopping-items"
                        />
                      </Box>
                      {splitMeals.saved_split_options_included ? (
                        <Alert severity="info" sx={{ mt: 2 }} data-testid="pantry-aware-split-meals-included">
                          {t("smart_kitchen.pantry_aware_preview.split_meals_included", {
                            count: splitMeals.alternative_recipe_count || splitMeals.split_meal_recipe_ids?.length || 0,
                          })}
                        </Alert>
                      ) : null}
                    </StatCard>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <StatCard icon={<TipsAndUpdatesRoundedIcon color="primary" />} title={t("smart_kitchen.pantry_aware_preview.recommendations_title")} testId="pantry-aware-recommendations-card">
                      <SimpleList
                        items={recommendations}
                        emptyText={t("smart_kitchen.pantry_aware_preview.no_recommendations")}
                        renderItem={(item) => recommendationText(item, t)}
                      />
                    </StatCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StatCard icon={<WarningAmberRoundedIcon color="warning" />} title={t("smart_kitchen.pantry_aware_preview.warnings_title")} testId="pantry-aware-warnings-card">
                      <SimpleList
                        items={warnings}
                        emptyText={t("smart_kitchen.pantry_aware_preview.no_warnings")}
                        renderItem={(item) => warningText(item, t)}
                        testId="pantry-aware-warnings"
                      />
                    </StatCard>
                  </Grid>
                </Grid>
              </Stack>
            ) : (
              <Card sx={{ borderRadius: 1 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    {t("smart_kitchen.pantry_aware_preview.empty_title")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("smart_kitchen.pantry_aware_preview.empty_body")}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}
